/**
 * InventoryDocumentForm — Phase 20 / 25
 *
 * Reusable document form for inventory entry / exit operations.
 *
 * Props:
 *   mode        — 'entry' | 'exit'
 *   companyId   — active company UUID
 *   vatRate     — company VAT % (e.g. 17)
 *   showToast   — shared toast callback
 *
 * Entry mode ('כניסה למלאי'):
 *   Shows: Supplier dropdown + Target Warehouse dropdown
 *   Balance effect: +qty via process_inventory_document RPC
 *
 * Exit mode ('יציאה מהמלאי'):
 *   Shows: Source Warehouse dropdown (supplier hidden)
 *   Balance effect: -qty via process_inventory_document RPC
 *
 * Document type is resolved automatically by matching the Hebrew name
 * ('תעודת כניסה למלאי' / 'תעודת יציאה מהמלאי') for the company.
 *
 * Phase 25 additions:
 *   • has_line_discount flag on DocumentType (from DB)
 *   • discount_pct field on each DocLine
 *   • updateLine recomputes: line_total = qty * unit_price * (1 - discount_pct/100)
 *   • Discount column (%) rendered conditionally when has_line_discount is true
 *   • discount_pct sent in payload lines array
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useDocLineSort, sortDocLines } from '@/hooks/useDocLineSort';
import { SortableColHeader } from '@/components/ui/SortableColHeader';
import type { InventoryPrintData } from '@/components/inventory/InventoryDocumentPrintView';
import { Plus, Trash2, Loader as Loader2, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Calendar, Truck, Warehouse as WarehouseIcon, PackagePlus, PackageMinus, FileText, ChevronDown, Hash, Percent, Download } from 'lucide-react';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import DocumentPickerModal, { type ImportableLine } from '@/components/modals/DocumentPickerModal';
import { supabase } from '@/lib/supabase';
import { dispatchNotification } from '@/lib/notificationPreferencesService';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Supplier {
  id:   string;
  name: string;
}

interface Warehouse {
  id:   string;
  name: string;
}

interface Product {
  id:   string;
  sku:  string;
  name: string;
  cost: number | null;
  price: number;
}

interface DocumentType {
  id:               string;
  name:             string;
  next_number:      number;
  prefix:           string;
  is_vat_liable:    boolean;
  has_line_discount: boolean;   // Phase 25
}

interface DocLine {
  product_id:   string;
  quantity:     string;   // string to allow free typing
  unit_price:   string;
  discount_pct: string;   // Phase 25 — "0" … "100"
  line_total:   number;   // computed
}

const EMPTY_LINE: DocLine = {
  product_id:   '',
  quantity:     '1',
  unit_price:   '0',
  discount_pct: '0',
  line_total:   0,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const today = () => new Date().toISOString().slice(0, 10);

function parseNum(s: string): number {
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

function fmtMoney(n: number): string {
  return n.toFixed(2);
}

/** Compute line total respecting optional discount */
function computeLineTotal(quantity: string, unit_price: string, discount_pct: string): number {
  const qty   = parseNum(quantity);
  const price = parseNum(unit_price);
  const disc  = Math.min(Math.max(parseNum(discount_pct), 0), 100); // clamp 0-100
  return qty * price * (1 - disc / 100);
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  mode:                  'entry' | 'exit';
  companyId:             string;
  vatRate:               number;
  showToast:             (msg: string, ok?: boolean) => void;
  docTypeOverride?:      DocumentType;
  onSuccess?:            (docTypeName: string, docRef: string, printData: InventoryPrintData) => void;
  onBeforeFirstSubmit?:  () => Promise<boolean>;
  initialLines?:         ImportableLine[];
}

export function InventoryDocumentForm({ mode, companyId, vatRate, showToast, docTypeOverride, onSuccess, onBeforeFirstSubmit, initialLines }: Props) {

  const DOC_TYPE_NAME = mode === 'entry'
    ? 'תעודת כניסה למלאי'
    : 'תעודת יציאה מהמלאי';

  // ── Reference data ────────────────────────────────────────────────────────
  const [suppliers,   setSuppliers]   = useState<Supplier[]>([]);
  const [warehouses,  setWarehouses]  = useState<Warehouse[]>([]);
  const [products,    setProducts]    = useState<Product[]>([]);
  const [docType,     setDocType]     = useState<DocumentType | null>(docTypeOverride ?? null);
  const [refLoading,  setRefLoading]  = useState(!docTypeOverride);
  const [refError,    setRefError]    = useState<string | null>(null);

  const fetchRefData = useCallback(async () => {
    if (!companyId) return;
    setRefLoading(true); setRefError(null);
    try {
      const supRes  = await supabase
        .from('inventory_suppliers')
        .select('id, name')
        .eq('company_id', companyId)
        .order('name');

      const whRes   = await supabase
        .from('inventory_warehouses')
        .select('id, name')
        .eq('company_id', companyId)
        .eq('is_external', false)
        .order('name');

      const prodRes = await supabase
        .from('inventory_products')
        .select('id, sku, name, cost, price')
        .eq('company_id', companyId)
        .order('name');

      if (supRes.error)  throw new Error(`ספקים: ${supRes.error.message}`);
      if (whRes.error)   throw new Error(`מחסנים: ${whRes.error.message}`);
      if (prodRes.error) throw new Error(`מוצרים: ${prodRes.error.message}`);

      setSuppliers(  (supRes.data  ?? []) as Supplier[]);
      setWarehouses( (whRes.data   ?? []) as Warehouse[]);
      setProducts(   (prodRes.data ?? []) as Product[]);

      // Only fetch document type when not supplied by the parent
      if (!docTypeOverride) {
        const dtRes = await supabase
          .from('document_types')
          .select('id, name, prefix, next_number, is_vat_liable, has_line_discount')
          .eq('company_id', companyId)
          .eq('name', DOC_TYPE_NAME)
          .maybeSingle();
        if (dtRes.error) throw new Error(`סוג מסמך: ${dtRes.error.message}`);
        setDocType(dtRes.data as DocumentType | null);
      }
    } catch (e) {
      setRefError(e instanceof Error ? e.message : 'שגיאה בטעינת נתונים');
    } finally {
      setRefLoading(false);
    }
  }, [companyId, DOC_TYPE_NAME, docTypeOverride]);

  useEffect(() => { fetchRefData(); }, [fetchRefData]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return;
      supabase.from('profiles').select('full_name').eq('id', user.id).maybeSingle()
        .then(({ data }) => { if (data?.full_name) setIssuedByName(data.full_name); });
    });
  }, []);

  // ── Form state ─────────────────────────────────────────────────────────────
  const [issueDate,   setIssueDate]   = useState<string>(today());
  const [supplierId,  setSupplierId]  = useState<string>('');
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [notes,       setNotes]       = useState<string>('');
  const [lines,       setLines]       = useState<DocLine[]>(() => {
    if (initialLines?.length) {
      return initialLines.map(l => ({
        product_id:   l.product_id,
        quantity:     String(l.quantity),
        unit_price:   String(l.unit_price),
        discount_pct: String(l.discount_pct ?? 0),
        line_total:   l.line_total,
      }));
    }
    return [{ ...EMPTY_LINE }];
  });
  const [showDocPicker, setShowDocPicker] = useState(false);
  const [formError,   setFormError]   = useState<string | null>(null);
  const [submitting,  setSubmitting]  = useState(false);
  const [issuedByName, setIssuedByName] = useState('');
  const lineSort = useDocLineSort();

  const handleSortLines = useCallback((key: string) => {
    const k = key as import('@/hooks/useDocLineSort').DocLineSortKey;
    const nextDir = lineSort.sort?.key === k && lineSort.sort.dir === 'asc' ? 'desc' as const : 'asc' as const;
    lineSort.toggle(k);
    setLines(prev => sortDocLines(prev, { key: k, dir: nextDir }, products));
  }, [lineSort, products]);

  const showDiscount = docType?.has_line_discount ?? false;

  // ── Product lookup map ────────────────────────────────────────────────────
  const productMap = useMemo(() => {
    const m: Record<string, Product> = {};
    for (const p of products) m[p.id] = p;
    return m;
  }, [products]);

  // ── Line helpers ──────────────────────────────────────────────────────────
  const updateLine = (idx: number, patch: Partial<DocLine>) => {
    setLines(prev => {
      const next = prev.map((l, i) => {
        if (i !== idx) return l;
        const merged = { ...l, ...patch };
        merged.line_total = computeLineTotal(
          merged.quantity,
          merged.unit_price,
          merged.discount_pct,
        );
        return merged;
      });
      return next;
    });
  };

  const addLine = () => setLines(prev => [...prev, { ...EMPTY_LINE }]);

  const removeLine = (idx: number) =>
    setLines(prev => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));

  // When user picks a product — auto-fill unit_price from product cost
  const handleProductSelect = (idx: number, productId: string) => {
    const prod = productMap[productId];
    const defaultPrice = prod
      ? (prod.cost ?? 0)        // always use cost — exits are valued at cost (accounting rule)
      : 0;
    updateLine(idx, {
      product_id: productId,
      unit_price: fmtMoney(defaultPrice),
    });
  };

  // ── Totals ────────────────────────────────────────────────────────────────
  const subtotal = useMemo(
    () => lines.reduce((sum, l) => sum + l.line_total, 0),
    [lines]
  );

  const isVatLiable = docType?.is_vat_liable ?? true;
  const vatAmount   = isVatLiable ? subtotal * (vatRate / 100) : 0;
  const total       = subtotal + vatAmount;

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = (): string | null => {
    if (!docType) return 'סוג מסמך לא נמצא — הרץ את ה-SQL של Phase 18.1 ו-Phase 20 תחילה';
    if (!warehouseId) return 'יש לבחור מחסן';
    if (lines.length === 0) return 'יש להוסיף לפחות שורה אחת';
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i]!;
      if (!l.product_id) return `שורה ${i + 1}: יש לבחור מוצר`;
      const qty = parseNum(l.quantity);
      if (qty <= 0) return `שורה ${i + 1}: כמות חייבת להיות גדולה מאפס`;
      const disc = parseNum(l.discount_pct);
      if (disc < 0 || disc > 100) return `שורה ${i + 1}: הנחה חייבת להיות בין 0 ל-100`;
    }
    return null;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const err = validate();
    if (err) { setFormError(err); return; }
    // Phase 80: intercept before first document submission
    if (onBeforeFirstSubmit) {
      const proceed = await onBeforeFirstSubmit();
      if (!proceed) return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      const payload = {
        company_id:       companyId,
        document_type_id: docType!.id,
        warehouse_id:     warehouseId,
        supplier_id:      supplierId || null,
        issued_by_name:   issuedByName || null,
        issue_date:       issueDate,
        subtotal:         subtotal,
        vat_amount:       vatAmount,
        total:            total,
        notes:            notes.trim() || null,
        lines: lines.map(l => ({
          product_id:   l.product_id,
          quantity:     parseNum(l.quantity),
          unit_price:   parseNum(l.unit_price),
          discount_pct: parseNum(l.discount_pct),
          line_total:   l.line_total,
        })),
      };

      const { data, error: rpcErr } = await supabase.rpc(
        'process_inventory_document',
        { payload },
      );

      if (rpcErr) throw rpcErr;

      const docNumber = docType!.next_number;
      const docRef    = `${(docType!.prefix) || ''}${docNumber}`;
      showToast(`${DOC_TYPE_NAME} ${docRef} הופק בהצלחה`, true);

      // Notify all company admins/editors about new document
      const { data: { user } } = await supabase.auth.getUser();
      const { data: members } = await supabase
        .from('company_members')
        .select('user_id')
        .eq('company_id', companyId);
      if (user && members?.length) {
        dispatchNotification({
          event_key:  'inventory.document_created',
          company_id: companyId,
          user_ids:   members.map((m: { user_id: string }) => m.user_id),
          title:      `מסמך חדש הופק: ${docRef}`,
          message:    `${DOC_TYPE_NAME} ${docRef} הופק בהצלחה`,
          action_url: '/app/inventory/movements',
          payload:    { doc_ref: docRef, doc_type: DOC_TYPE_NAME },
        });
      }

      if (onSuccess) {
        const printData: InventoryPrintData = {
          docTypeName:  docType!.name,
          docNumber:    docRef,
          date:         issueDate,
          lines: lines.map(l => ({
            product_id:   l.product_id,
            product_name: productMap[l.product_id]?.name ?? l.product_id,
            quantity:     parseNum(l.quantity),
            unit_price:   parseNum(l.unit_price),
            discount_pct: parseNum(l.discount_pct),
            line_total:   l.line_total,
          })),
          subtotal,
          vatAmount,
          grandTotal:  total,
          vatRate:     docType!.is_vat_liable ? vatRate : 0,
          documentId:  typeof data === 'string' ? data : undefined,
          issuedByName: issuedByName || undefined,
        };
        onSuccess(docType!.name, docRef, printData);
        return; // parent handles reset via SuccessState
      }

      // Reset form (standalone usage without parent)
      setIssueDate(today());
      setSupplierId('');
      setWarehouseId('');
      setNotes('');
      setLines([{ ...EMPTY_LINE }]);
      // Refresh doc type to get updated next_number
      fetchRefData();

      // Suppress unused variable warning (data is the returned uuid)
      void data;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading / error states ────────────────────────────────────────────────
  if (refLoading) {
    return (
      <div className="flex items-center justify-center py-20 gap-3 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">טוען נתונים…</span>
      </div>
    );
  }

  if (refError) {
    return (
      <div className="flex items-center gap-3 m-6 px-4 py-3 bg-red-50 border border-red-200
                      rounded-xl text-sm text-red-700">
        <AlertCircle className="w-4 h-4 shrink-0" />{refError}
      </div>
    );
  }

  // ── Accent colours per mode ───────────────────────────────────────────────
  const accent = mode === 'entry'
    ? { bg: 'bg-emerald-600', hover: 'hover:bg-emerald-700', ring: 'focus:ring-emerald-400',
        light: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700',
        icon: <PackagePlus className="w-5 h-5 text-emerald-600" /> }
    : { bg: 'bg-rose-600',    hover: 'hover:bg-rose-700',    ring: 'focus:ring-rose-400',
        light: 'bg-rose-50',   border: 'border-rose-200',     text: 'text-rose-700',
        icon: <PackageMinus className="w-5 h-5 text-rose-600" /> };

  const title    = mode === 'entry' ? 'כניסה למלאי' : 'יציאה מהמלאי';
  const subtitle = mode === 'entry'
    ? 'קליטת סחורה ממחסן — עדכון יתרות כניסה'
    : 'הוצאת סחורה מהמחסן — עדכון יתרות יציאה';

  // ── Grid column template ──────────────────────────────────────────────────
  // With discount:    product | qty | unit_price | discount | line_total | remove
  // Without discount: product | qty | unit_price | line_total | remove
  const gridCols = showDiscount
    ? 'grid-cols-[1fr_7rem_8rem_6rem_8rem_2.5rem]'
    : 'grid-cols-[1fr_7rem_8rem_8rem_2.5rem]';

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5" dir="rtl">

      {/* ── Section header ── */}
      <div className={`flex items-center justify-between px-6 py-4
                       ${accent.light} border-b ${accent.border} rounded-t-2xl`}>
        <div className="flex items-center gap-3">
          {accent.icon}
          <div>
            <h2 className={`text-base font-semibold ${accent.text}`}>{title}</h2>
            <p className="text-xs text-gray-500">{subtitle}</p>
          </div>
        </div>

        {/* Document type + next number badge */}
        {docType ? (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg
                          border border-gray-200 shadow-sm">
            <Hash className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs text-gray-500">מספר הבא:</span>
            <span className="font-mono font-bold text-orange-600 text-sm tracking-wide">
              {(docType.prefix || '') + docType.next_number}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-lg
                          border border-amber-200 text-xs text-amber-700">
            <AlertCircle className="w-3.5 h-3.5" />
            סוג מסמך לא נמצא
          </div>
        )}
      </div>

      {/* ── Form body ── */}
      <div className="px-6 space-y-6 pb-6">

        {/* Form error */}
        {formError && (
          <div className="flex items-start gap-2.5 px-4 py-3 bg-red-50 border border-red-200
                          rounded-xl text-sm text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{formError}</span>
          </div>
        )}

        {/* ── Header fields grid ── */}
        <div className={`grid gap-4 ${mode === 'entry' ? 'grid-cols-3' : 'grid-cols-2'}`}>

          {/* Issue date */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              תאריך הפקה
            </label>
            <input
              type="date"
              value={issueDate}
              onChange={e => setIssueDate(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                         focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              dir="ltr"
            />
          </div>

          {/* Supplier — entry only */}
          {mode === 'entry' && (
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                <Truck className="w-3.5 h-3.5" />
                ספק
                <span className="text-gray-400 normal-case font-normal">(אופציונלי)</span>
              </label>
              <SearchableSelect
                options={suppliers.map(s => ({ value: s.id, label: s.name }))}
                value={supplierId}
                onChange={setSupplierId}
                placeholder="— בחר ספק —"
              />
              {suppliers.length === 0 && (
                <p className="text-xs text-amber-600">אין ספקים — הוסף ספקים בהגדרות המלאי</p>
              )}
            </div>
          )}

          {/* Warehouse */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              <WarehouseIcon className="w-3.5 h-3.5" />
              {mode === 'entry' ? 'מחסן יעד' : 'מחסן מקור'}
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={warehouseId}
                onChange={e => setWarehouseId(e.target.value)}
                className="w-full appearance-none px-3 py-2.5 border border-gray-300 rounded-lg
                           text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400
                           focus:border-transparent pl-8"
              >
                <option value="">— בחר מחסן —</option>
                {warehouses.map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4
                                      text-gray-400 pointer-events-none" />
            </div>
            {warehouses.length === 0 && (
              <p className="text-xs text-amber-600">אין מחסנים — הוסף מחסנים בהגדרות המלאי</p>
            )}
          </div>
        </div>

        {/* ── Lines table ── */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" />
              שורות מסמך
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowDocPicker(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                           text-orange-600 hover:text-orange-700 bg-orange-50 hover:bg-orange-100
                           rounded-lg transition-colors border border-orange-200"
              >
                <Download className="w-3.5 h-3.5" />
                ייבא ממסמך
              </button>
              <button
                type="button"
                onClick={addLine}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                           text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100
                           rounded-lg transition-colors border border-blue-200"
              >
                <Plus className="w-3.5 h-3.5" />
                הוסף שורה
              </button>
            </div>
          </div>

          {showDocPicker && (
            <DocumentPickerModal
              companyId={companyId}
              onClose={() => setShowDocPicker(false)}
              onSelect={(imported) => {
                const newLines: DocLine[] = imported.map(l => ({
                  product_id:   l.product_id,
                  quantity:     String(l.quantity),
                  unit_price:   String(l.unit_price),
                  discount_pct: String(l.discount_pct ?? 0),
                  line_total:   l.line_total,
                }));
                setLines(prev => {
                  const hasContent = prev.some(l => l.product_id !== '');
                  return hasContent ? [...prev, ...newLines] : newLines;
                });
                setShowDocPicker(false);
              }}
            />
          )}

          {/* Lines header */}
          <div className={`grid ${gridCols} gap-2 px-3
                          text-xs font-semibold text-gray-500 uppercase tracking-wide`}>
            <SortableColHeader label="מוצר" sortKey="product_name" activeKey={lineSort.sort?.key ?? null} dir={lineSort.sort?.dir ?? null} onSort={handleSortLines} />
            <SortableColHeader label="כמות" sortKey="quantity" activeKey={lineSort.sort?.key ?? null} dir={lineSort.sort?.dir ?? null} onSort={handleSortLines} className="justify-center" />
            <SortableColHeader label={mode === 'entry' ? 'עלות (ללא מע״מ)' : 'מחיר (ללא מע״מ)'} sortKey="unit_price" activeKey={lineSort.sort?.key ?? null} dir={lineSort.sort?.dir ?? null} onSort={handleSortLines} className="justify-center" />
            {showDiscount && (
              <SortableColHeader label="הנחה" sortKey="discount_pct" activeKey={lineSort.sort?.key ?? null} dir={lineSort.sort?.dir ?? null} onSort={handleSortLines} className="justify-center">
                <Percent className="w-3 h-3" />
              </SortableColHeader>
            )}
            <SortableColHeader label="סה״כ שורה" sortKey="line_total" activeKey={lineSort.sort?.key ?? null} dir={lineSort.sort?.dir ?? null} onSort={handleSortLines} />
            <span />
          </div>

          {/* Lines */}
          <div className="space-y-2">
            {lines.map((line, idx) => (
              <div
                key={idx}
                className={`grid ${gridCols} gap-2 items-center
                           bg-gray-50/70 px-3 py-2.5 rounded-xl border border-gray-100`}
              >
                {/* Product select */}
                <SearchableSelect
                  options={products.map(p => ({ value: p.id, label: p.name, secondary: p.sku }))}
                  value={line.product_id}
                  onChange={v => handleProductSelect(idx, v)}
                  placeholder="— בחר מוצר —"
                  compact
                />

                {/* Quantity */}
                <input
                  type="number"
                  min="0.001"
                  step="0.001"
                  value={line.quantity}
                  onChange={e => updateLine(idx, { quantity: e.target.value })}
                  className="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-sm
                             text-center font-mono focus:outline-none focus:ring-2 focus:ring-blue-400
                             focus:border-transparent"
                  dir="ltr"
                />

                {/* Unit price */}
                <div className="relative">
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400
                                   text-xs pointer-events-none">₪</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={line.unit_price}
                    onChange={e => updateLine(idx, { unit_price: e.target.value })}
                    className="w-full pr-7 pl-2.5 py-2 border border-gray-200 rounded-lg text-sm
                               font-mono focus:outline-none focus:ring-2 focus:ring-blue-400
                               focus:border-transparent"
                    dir="ltr"
                  />
                </div>

                {/* Discount % — conditional */}
                {showDiscount && (
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={line.discount_pct}
                      onChange={e => updateLine(idx, { discount_pct: e.target.value })}
                      className="w-full pl-6 pr-2.5 py-2 border border-amber-200 rounded-lg
                                 text-sm font-mono text-center focus:outline-none focus:ring-2
                                 focus:ring-amber-400 focus:border-transparent bg-amber-50/40"
                      dir="ltr"
                      placeholder="0"
                    />
                    <Percent className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3
                                        text-amber-400 pointer-events-none" />
                  </div>
                )}

                {/* Line total (read-only) */}
                <div className="px-2.5 py-2 bg-white border border-gray-100 rounded-lg
                                text-sm font-mono font-semibold text-gray-700 text-left tabular-nums"
                     dir="ltr">
                  ₪{fmtMoney(line.line_total)}
                </div>

                {/* Remove button */}
                <button
                  type="button"
                  onClick={() => removeLine(idx)}
                  disabled={lines.length === 1}
                  className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50
                             rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="הסר שורה"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* ── Notes ── */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            הערות
            <span className="normal-case font-normal text-gray-400 mr-1">(אופציונלי)</span>
          </label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            placeholder="הערות לתעודה…"
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm resize-none
                       focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
          />
        </div>

        {/* ── Totals footer ── */}
        <div className="flex items-end justify-between gap-6 pt-2 border-t border-gray-100">

          {/* Left: submit button */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !docType}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-white text-sm
                        font-semibold transition-colors shadow-sm
                        ${accent.bg} ${accent.hover}
                        disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {submitting
              ? <><Loader2 className="w-4 h-4 animate-spin" />מעבד…</>
              : <><CheckCircle2 className="w-4 h-4" />הפק תעודה</>
            }
          </button>

          {/* Right: totals */}
          <div className="space-y-1.5 text-sm min-w-[220px]" dir="ltr">
            <div className="flex items-center justify-between gap-8 text-gray-600">
              <span className="text-right">סה״כ לפני מע״מ</span>
              <span className="font-mono font-semibold">₪{fmtMoney(subtotal)}</span>
            </div>
            <div className="flex items-center justify-between gap-8 text-gray-500 text-xs">
              <span className="text-right">
                מע״מ {isVatLiable ? `${vatRate}%` : '(פטור)'}
              </span>
              <span className="font-mono">₪{fmtMoney(vatAmount)}</span>
            </div>
            <div className="flex items-center justify-between gap-8 pt-1.5 border-t border-gray-200
                            font-bold text-gray-900">
              <span className="text-right">סה״כ לתשלום</span>
              <span className="font-mono text-base">₪{fmtMoney(total)}</span>
            </div>
          </div>
        </div>

      </div>{/* end form body */}
    </div>
  );
}
